import { createTheme } from '@mui/material/styles';
import radius from './radius';

const nexviaTheme = createTheme({
  palette: {
    primary: {
      main: '#2261E2',      // --azul-50
      dark: '#184BB4',      // --azul-40
      light: '#4B7EE7',     // --azul-60
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#2E3132',   // --gris-20
      secondary: '#757779', // --gris-50
      disabled: '#AAABAD',  // --gris-70
      dark: '#2E3132',      // legacy compat — used by AppBar + layout components
      hint: '#F0F1F3',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    grey: {
      50:  '#F8F9FB',       // --gris-98
      100: '#F0F1F3',       // --gris-95
      200: '#E1E2E4',       // --gris-90
      300: '#C5C6C8',       // --gris-80
      400: '#AAABAD',       // --gris-70
      500: '#8F9193',       // --gris-60
      600: '#757779',       // --gris-50
      700: '#5C5F60',       // --gris-40
      800: '#454749',       // --gris-30
      900: '#2E3132',       // --gris-20
    },
    divider: '#E1E2E4',     // --gris-90
    error: {
      main: '#C62828',
      light: '#FFEBEE',
    },
    success: {
      main: '#2E7D32',
      light: '#E8F5E9',
    },
    warning: {
      main: '#E65100',
      light: '#FFF3E0',
    },
  },
  typography: {
    fontFamily: '"Inter", Arial, sans-serif',

    // ── Legacy custom properties — used by MainLayout header/sidebar components ──
    commonAvatar: { cursor: 'pointer', borderRadius: '50%' },
    smallAvatar:  { width: '22px', height: '22px', fontSize: '1rem' },
    mediumAvatar: { width: '34px', height: '34px', fontSize: '1.2rem' },
    largeAvatar:  { width: '44px', height: '44px', fontSize: '1.5rem' },
    menuCaption: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#2E3132',
      padding: '6px',
      textTransform: 'capitalize',
      marginTop: '10px',
    },
    subMenuCaption: {
      fontSize: '0.6875rem',
      fontWeight: 500,
      color: '#757779',
      textTransform: 'capitalize',
    },
    customInput: {
      marginTop: 1,
      marginBottom: 1,
      '& > label': {
        top: 23,
        left: 0,
        color: '#757779',
        '&[data-shrink="false"]': { top: 5 },
      },
      '& > div > input': { padding: '30.5px 14px 11.5px !important' },
      '& legend': { display: 'none' },
      '& fieldset': { top: 0 },
    },
    mainContent: {
      backgroundColor: '#F0F1F3',
      width: '100%',
      minHeight: 'calc(100vh - 88px)',
      flexGrow: 1,
      padding: '20px',
      marginTop: '88px',
      marginRight: '20px',
      borderRadius: '8px',
    },
    // ─────────────────────────────────────────────────────────────────────────
    h1: { fontSize: '2.125rem', fontWeight: 700, letterSpacing: '-0.0625rem' },
    h2: { fontSize: '1.5rem',   fontWeight: 700, letterSpacing: '-0.046875rem' },
    h3: { fontSize: '1.25rem',  fontWeight: 600, letterSpacing: '-0.03125rem' },
    h4: { fontSize: '1rem',     fontWeight: 600, letterSpacing: '-0.015625rem' },
    h5: { fontSize: '0.875rem', fontWeight: 500, letterSpacing: '-0.009375rem' },
    h6: { fontSize: '0.75rem',  fontWeight: 500 },
    body1: { fontSize: '1rem',     fontWeight: 400, lineHeight: 1.25, letterSpacing: '-0.015625rem' },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43, letterSpacing: '-0.009375rem' },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.75rem',  fontWeight: 400, color: '#757779' },
    caption:   { fontSize: '0.75rem',  fontWeight: 400, color: '#757779' },
    button: {
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '-0.015625rem',
    },

    // ── Escalas del design system (Nexvia App - Rediseño) ──
    // Usar via <Typography variant="displayLarge" /> etc. No reemplazan h1-h6/body1-2,
    // que siguen usándose en pantallas no migradas todavía.
    displayLarge: {
      fontSize: 80, lineHeight: '80px', letterSpacing: '-4px', fontWeight: 700,
      '@media (max-width: 767px)': { fontSize: 44, lineHeight: '44px', letterSpacing: '-2px' },
    },
    displayMedium: {
      fontSize: 64, lineHeight: '64px', letterSpacing: '-3px', fontWeight: 700,
      '@media (max-width: 767px)': { fontSize: 36, lineHeight: '36px', letterSpacing: '-1.5px' },
    },
    displaySmall: {
      fontSize: 48, lineHeight: '48px', letterSpacing: '-2px', fontWeight: 700,
      '@media (max-width: 767px)': { fontSize: 32, lineHeight: '32px', letterSpacing: '-1px' },
    },
    titleXlarge: {
      fontSize: 40, lineHeight: '46px', letterSpacing: '-1.5px', fontWeight: 600,
      '@media (max-width: 767px)': { fontSize: 28, lineHeight: '34px', letterSpacing: '-0.75px' },
    },
    titleLarge: {
      fontSize: 32, lineHeight: '38px', letterSpacing: '-1.5px', fontWeight: 600,
      '@media (max-width: 767px)': { fontSize: 24, lineHeight: '28px', letterSpacing: '-0.75px' },
    },
    titleMedium: {
      fontSize: 24, lineHeight: '30px', letterSpacing: '-1px', fontWeight: 600,
      '@media (max-width: 767px)': { fontSize: 20, lineHeight: '24px', letterSpacing: '-0.5px' },
    },
    titleSmall: {
      fontSize: 18, lineHeight: '22px', letterSpacing: '-0.5px', fontWeight: 600,
      '@media (max-width: 767px)': { fontSize: 16, lineHeight: '20px', letterSpacing: '-0.25px' },
    },
    bodyLarge: {
      fontSize: 20, lineHeight: '28px', letterSpacing: '-0.5px', fontWeight: 500,
      '@media (max-width: 767px)': { fontSize: 18, lineHeight: '26px' },
    },
    bodyMedium: { fontSize: 16, lineHeight: '20px', letterSpacing: '-0.25px', fontWeight: 500 },
    bodySmall: { fontSize: 14, lineHeight: '16px', letterSpacing: '-0.15px', fontWeight: 500 },
    labelLarge: {
      fontSize: 20, lineHeight: '24px', letterSpacing: '-0.5px', fontWeight: 500,
      '@media (max-width: 767px)': { fontSize: 18, lineHeight: '22px' },
    },
    labelMedium: { fontSize: 16, lineHeight: '20px', letterSpacing: '-0.25px', fontWeight: 500 },
    labelSmall: { fontSize: 14, lineHeight: '18px', letterSpacing: '-0.15px', fontWeight: 500 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: `${radius.pill}px`,
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        },
        contained: {
          '&:hover': { backgroundColor: '#184BB4' },
          '&:active': { backgroundColor: '#123887' },
        },
        outlined: {
          borderColor: '#2261E2',
          '&:hover': { borderColor: '#184BB4', color: '#184BB4', backgroundColor: 'transparent' },
        },
        text: {
          '&:hover': { backgroundColor: '#E8EFFC' },
          '&:active': { backgroundColor: '#D2DFF9', color: '#184BB4' },
        },
        sizeLarge: {
          padding: '24px 40px',
          fontSize: '1.25rem',
          lineHeight: 1.2,
          letterSpacing: '-0.03125rem',
          gap: '16px',
          '@media (max-width: 767px)': { padding: '16px 40px', gap: '12px' },
        },
        sizeSmall: {
          padding: '12px 20px',
          fontSize: '0.875rem',
          lineHeight: 1.143,
          letterSpacing: '-0.009375rem',
          gap: '8px',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: '8px' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: `${radius.pill}px`, fontWeight: 500, fontFamily: '"Inter", Arial, sans-serif' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2261E2' },
        },
        notchedOutline: { borderColor: '#C5C6C8' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#757779',
          '&.Mui-focused': { color: '#2261E2' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: '8px' } },
    },
    MuiDialog: {
      // 16px = radius.card, no radius.panel (24px) — se mantiene el valor visual actual,
      // solo se referencia la constante para no duplicar el literal.
      styleOverrides: { paper: { borderRadius: `${radius.card}px` } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2E3132',
          borderRadius: '8px',
          fontSize: '0.75rem',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: { fontFamily: '"Inter", Arial, sans-serif' },
      },
    },
  },
});

export default nexviaTheme;
